import { ImaEvent, ImaStreamManager } from 'types/player';
import AbstractAdManager, {
  AdCuePoint,
} from 'components/pages/playback/ad-managers/AbstractAdManager';
import { getInteractiveAdData } from '../interactive-ads/InteractiveAdUtils';

const CUE_STARTING_TOLERANCE = 2;

export default class ImaAdManager extends AbstractAdManager {
  override readonly adManagerType = 'ima';
  private streamManager: ImaStreamManager | null = null;
  // Interactive (Truex) ads introduce a concept of placeholder ads, which we need to ignore
  // This information is only made known when starting an ad, keep track of it per new ad pod
  private currentCuePlaceholderTime = 0;
  private currentAd: any;
  private elapsedAdPlayed = 0;

  override _inactive(): void {
    super._inactive();
    this.streamManager = null;
  }

  setStreamManager(streamManager: ImaStreamManager) {
    this.streamManager = streamManager;
  }

  getCuePoint(time: number): AdCuePoint | null {
    const previousCuePoint =
      this.streamManager?.previousCuePointForStreamTime(time);
    if (!previousCuePoint) return null;

    const cuePoint = this._cuePoints?.find(
      cp =>
        cp.start === previousCuePoint.start && cp.end === previousCuePoint.end,
    );

    if (cuePoint) {
      // Use both local and IMA cue point to determine if played
      previousCuePoint.played = previousCuePoint.played || cuePoint.played;
    }

    return previousCuePoint;
  }

  getPreviousCuePoint(time: number): AdCuePoint | null {
    // For IMA, this is same as getCuePoint()
    return this.getCuePoint(time);
  }

  getNextCuePoint(): AdCuePoint | null {
    // Not used for IMA
    throw new Error('Method not implemented');
  }

  getCurrentCuePoint(): AdCuePoint | null {
    const playerTime = this.getRawTime();
    const cuePoint = this.getCuePoint(
      // If the player time = cue point start time, IMA returns the previous cue
      // point, despite being inside an ad. Using slight offset to account for
      // this behavior
      playerTime + CUE_STARTING_TOLERANCE,
    );

    if (!cuePoint || playerTime + CUE_STARTING_TOLERANCE < cuePoint.start) {
      return null;
    }
    return cuePoint;
  }

  onStreamEvent(event: any) {
    if (event.type === ImaEvent.AD_BREAK_STARTED) {
      this.currentCuePlaceholderTime = 0;
    } else if (event.type === ImaEvent.STARTED) {
      this.handleAdStarted(event);
      this.currentAd = event.getAd();
      this.elapsedAdPlayed = this.currentAdTime();
    } else if (event.type === ImaEvent.AD_BREAK_ENDED) {
      this.elapsedAdPlayed = 0;
      this.currentAd = null;
    } else if (event.type === ImaEvent.CUEPOINTS_CHANGED) {
      this.handleCuePointsChanged(event);
    }
  }

  handleAdStarted(event: any) {
    const ad: google.ima.Ad = event.getAd();

    const cuePoint = this.getCurrentCuePoint();

    const interactiveAdData = getInteractiveAdData(
      ad,
      cuePoint,
      this.getRawTime(),
    );
    if (interactiveAdData) {
      if (interactiveAdData.ignoreAdDuration) {
        this.currentCuePlaceholderTime += ad.getDuration();
      }

      this.signal('interactiveAdInit', interactiveAdData);
    }
  }

  handleCuePointsChanged(event: any) {
    const { cuepoints: imaCuePoints } = event.getStreamData();

    const cuePoints = this._cuePoints;

    if (cuePoints && cuePoints.length === imaCuePoints.length) {
      (imaCuePoints as Array<AdCuePoint>).forEach((imaCuePoint, index) => {
        // The local and IMA cue point indexes should match
        const cuePoint = cuePoints[index];

        if (
          !cuePoint ||
          cuePoint.start !== imaCuePoint.start ||
          cuePoint.end !== imaCuePoint.end
        ) {
          return;
        } else {
          imaCuePoint.played = imaCuePoint.played || cuePoint.played;
        }
      });
    }

    this._cuePoints = imaCuePoints;
    this.signal('cuePointsChanged', this._cuePoints);
  }

  override adDuration() {
    const duration = super.adDuration();
    if (!duration) return duration;

    return Math.max(duration - this.currentCuePlaceholderTime, 0);
  }

  override currentAdTime(): number {
    return Math.max(super.currentAdTime() - this.currentCuePlaceholderTime, 0);
  }

  override currentAdTimeRemaining(): number {
    if (!this.currentAd) return 0;

    const timeWatchedCurrentAd = this.currentAdTime() - this.elapsedAdPlayed;
    const timeRemaining = this.currentAd.getDuration() - timeWatchedCurrentAd;
    return Math.max(0, timeRemaining);
  }
}
